import { uniq } from "lodash-es";
import { useMemo } from "react";

import type { ConnectedProjectDto, ProjectOverviewItemDto } from "@/api/types";
import { filterProjectsByFilters } from "@/components/ProjectFilter/filterProjectsByFilters";
import type { ProjectFilterValues } from "@/components/ProjectFilter/ProjectFilter";

export function useProjectFilterOptions(
  projects: (ConnectedProjectDto | ProjectOverviewItemDto)[],
  filters: ProjectFilterValues,
): {
  owners: string[];
  maintainers: string[];
  cities: string[];
  amountUniqueOwners: number;
  amountUniqueMaintainers: number;
  amountUniqueCities: number;
} {
  const { amountUniqueOwners, amountUniqueMaintainers, amountUniqueCities } = useMemo(() => {
    return {
      amountUniqueOwners: uniq(projects.map((p) => p.owner)).length,
      amountUniqueMaintainers: uniq(projects.map((p) => p.maintenance)).length,
      amountUniqueCities: uniq(projects.map((p) => p.city)).length,
    };
  }, [projects]);

  const { owners, maintainers, cities } = useMemo(() => {
    const owners = uniq(filterProjectsByFilters(projects, { ...filters, owner: undefined }).map((p) => p.owner)).sort(
      (a, b) => a.localeCompare(b),
    );
    const maintainers = uniq(
      filterProjectsByFilters(projects, { ...filters, maintenance: undefined }).map((p) => p.maintenance),
    ).sort((a, b) => a.localeCompare(b));
    const cities = uniq(filterProjectsByFilters(projects, { ...filters, city: undefined }).map((p) => p.city)).sort(
      (a, b) => a.localeCompare(b),
    );

    return { owners, maintainers, cities };
  }, [projects, filters]);

  return {
    owners,
    maintainers,
    cities,
    amountUniqueOwners,
    amountUniqueMaintainers,
    amountUniqueCities,
  };
}
